export interface UploadProgress {
  phase: 'preparing' | 'uploading' | 'saving';
  loaded?: number;
  total?: number;
}

/** Same signed PUT as the normal upload, with browser byte events when requested. */
export async function putStorage(url: string, blob: Blob, signal: AbortSignal, headers?: Record<string, string>, onBytes?: (loaded: number, total: number) => void): Promise<{ ok: boolean; status: number; etag: string }> {
  signal.throwIfAborted();
  if (!onBytes) {
    const response = await fetch(url, { method: 'PUT', body: blob, signal, ...(headers ? { headers } : {}) });
    return { ok: response.ok, status: response.status, etag: response.headers.get('ETag') || '' };
  }
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const cleanup = () => signal.removeEventListener('abort', abort);
    const abort = () => xhr.abort();
    xhr.open('PUT', url);
    for (const [key, value] of Object.entries(headers || {})) xhr.setRequestHeader(key, value);
    xhr.upload.onprogress = event => onBytes(Math.min(event.loaded, blob.size), blob.size);
    xhr.onload = () => {
      cleanup();
      const ok = xhr.status >= 200 && xhr.status < 300;
      if (ok) onBytes(blob.size, blob.size);
      resolve({ ok, status: xhr.status, etag: xhr.getResponseHeader('ETag') || '' });
    };
    xhr.onerror = () => { cleanup(); reject(new Error('Storage could not be reached. Check your connection and try again.')); };
    xhr.onabort = () => { cleanup(); reject(signal.reason || new DOMException('Upload cancelled.', 'AbortError')); };
    signal.addEventListener('abort', abort, { once: true });
    if (signal.aborted) { cleanup(); reject(signal.reason); return; }
    try { xhr.send(blob); } catch (error) { cleanup(); reject(error); }
  });
}
